/**
 * Debug Field Registry
 *
 * Extensible registry for debug field definitions.
 * Use this to register new fields that can be captured during processing.
 *
 * @example
 * // Register a new field
 * DebugFieldRegistry.getInstance().register({
 *   id: 'custom.myField',
 *   name: 'My Custom Field',
 *   category: 'revenue',
 *   description: 'A custom debug field',
 *   extractor: (ctx) => ctx.cashFlow?.someValue,
 *   performanceCost: 'low',
 *   enabledByDefault: false,
 * });
 */

import type {
  DebugCaptureConfig,
  DebugCapturePreset,
  DebugFieldCategory,
  DebugFieldDefinition,
} from './types';

/**
 * Preset configurations for field capture
 */
const PRESET_CONFIGS: Record<DebugCapturePreset, { patterns: string[]; excludeHighCost: boolean }> =
  {
    minimal: {
      patterns: [
        'revenue.total',
        'principal.total',
        'interest.total',
        'drf.ending',
        'metadata.paymentDate',
      ],
      excludeHighCost: true,
    },
    standard: {
      patterns: ['*'], // All default-enabled fields
      excludeHighCost: true,
    },
    full: {
      patterns: ['*'], // All fields including high-cost
      excludeHighCost: false,
    },
    custom: {
      patterns: [], // Configured via enabledFields
      excludeHighCost: false,
    },
  };

/**
 * Debug Field Registry - Singleton
 *
 * Manages registration and retrieval of debug field definitions.
 * Supports extensible field registration for future additions.
 */
export class DebugFieldRegistry {
  private static instance: DebugFieldRegistry | null = null;
  private fields: Map<string, DebugFieldDefinition> = new Map();
  private initialized = false;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get the singleton instance
   */
  static getInstance(): DebugFieldRegistry {
    if (!DebugFieldRegistry.instance) {
      DebugFieldRegistry.instance = new DebugFieldRegistry();
    }
    return DebugFieldRegistry.instance;
  }

  /**
   * Reset the singleton (for testing)
   */
  static reset(): void {
    DebugFieldRegistry.instance = null;
  }

  /**
   * Register a new debug field
   */
  register<T>(field: DebugFieldDefinition<T>): void {
    if (this.fields.has(field.id)) {
      console.warn(`[DebugFieldRegistry] Field '${field.id}' already registered, overwriting`);
    }
    this.fields.set(field.id, field as DebugFieldDefinition);
  }

  /**
   * Register multiple fields at once
   */
  registerMany(fields: DebugFieldDefinition[]): void {
    for (const field of fields) {
      this.register(field);
    }
  }

  /**
   * Get a field by ID
   */
  get(id: string): DebugFieldDefinition | undefined {
    return this.fields.get(id);
  }

  /**
   * Get all registered fields
   */
  getAll(): DebugFieldDefinition[] {
    return Array.from(this.fields.values());
  }

  /**
   * Get fields by category
   */
  getByCategory(category: DebugFieldCategory): DebugFieldDefinition[] {
    return this.getAll().filter((f) => f.category === category);
  }

  /**
   * Get fields enabled by default
   */
  getDefaultEnabled(): DebugFieldDefinition[] {
    return this.getAll().filter((f) => f.enabledByDefault);
  }

  /**
   * Get fields matching a pattern (supports wildcards)
   * @example
   * getByPattern('revenue.*') // All revenue fields
   * getByPattern('*.total')   // All fields ending with .total
   */
  getByPattern(pattern: string): DebugFieldDefinition[] {
    if (pattern === '*') {
      return this.getAll();
    }

    const regex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`);
    return this.getAll().filter((f) => regex.test(f.id));
  }

  /**
   * Get enabled fields based on configuration
   */
  getEnabledFields(config: DebugCaptureConfig): DebugFieldDefinition[] {
    const presetConfig = PRESET_CONFIGS[config.preset];

    // Start with pattern-matched fields
    let fields: DebugFieldDefinition[] = [];

    if (config.preset === 'custom' && config.enabledFields) {
      // Custom preset uses explicit field list
      fields = config.enabledFields
        .map((id) => this.get(id))
        .filter((f): f is DebugFieldDefinition => f !== undefined);
    } else {
      // Use preset patterns
      for (const pattern of presetConfig.patterns) {
        fields.push(...this.getByPattern(pattern));
      }

      // Filter to default-enabled for standard preset
      if (config.preset === 'standard') {
        fields = fields.filter((f) => f.enabledByDefault);
      }
    }

    // Exclude high-cost fields if configured
    if (presetConfig.excludeHighCost) {
      fields = fields.filter((f) => f.performanceCost !== 'high');
    }

    // Apply explicit disables
    if (config.disabledFields && config.disabledFields.length > 0) {
      const disabledSet = new Set(config.disabledFields);
      fields = fields.filter((f) => !disabledSet.has(f.id));
    }

    // Deduplicate by ID
    const seen = new Set<string>();
    return fields.filter((f) => {
      if (seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    });
  }

  /**
   * Get field IDs for a preset
   */
  getPresetFieldIds(preset: DebugCapturePreset): string[] {
    const config: DebugCaptureConfig = {
      preset,
      maxPayments: 100,
      captureBondBreakdown: true,
      captureFormulas: true,
    };
    return this.getEnabledFields(config).map((f) => f.id);
  }

  /**
   * Check if registry has been initialized with fields
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Mark registry as initialized
   */
  markInitialized(): void {
    this.initialized = true;
  }

  /**
   * Get count of registered fields
   */
  get size(): number {
    return this.fields.size;
  }

  /**
   * Get all categories with at least one field
   */
  getCategories(): DebugFieldCategory[] {
    const categories = new Set<DebugFieldCategory>();
    for (const field of this.fields.values()) {
      categories.add(field.category);
    }
    return Array.from(categories);
  }
}

/**
 * Convenience function to get the registry instance
 */
export function getFieldRegistry(): DebugFieldRegistry {
  return DebugFieldRegistry.getInstance();
}
